import React from "react";
import { Button as AntButton } from "antd";
import Tooltip from "../Tooltip/Tooltip";
import "./button.css";

/**
 * Wrapper component for Ant Design Button with optional Tooltip support
 *
 * This wrapper allows us to easily replace Ant Design in the future without changing all imports.
 * It preserves all Ant Design Button functionality including types, sizes, and loading states.
 * Additionally, it provides built-in tooltip support for convenience.
 *
 * @component
 * @example
 * import { Button } from '../GeneralComponents/ui';
 *
 * // Basic Button
 * <Button type="primary">Primary Button</Button>
 *
 * // Button with Tooltip
 * <Button tooltip="This is a tooltip" tooltipPlacement="top">
 *   Button with Tooltip
 * </Button>
 *
 * // Button with active state
 * <Button active="active" className="my-button">
 *   Active Button
 * </Button>
 *
 * @param {string} tooltip - Tooltip text to display (optional)
 * @param {string} tooltipPlacement - Tooltip placement (default: "top")
 * @param {string} overlayClassName - Custom className for tooltip overlay
 * @param {string} active - Active state className to append
 * @param {string} className - Additional className for the button
 * @param {...any} props - All other Ant Design Button props are supported
 *
 * @see https://ant.design/components/button/ - Ant Design Button documentation
 */
const Button = React.forwardRef((props, ref) => {
  const {
    tooltip,
    tooltipPlacement = "top",
    overlayClassName,
    active,
    className,
    getPopupContainer,
    ...buttonProps
  } = props;

  // Combine className with active state
  const combinedClassName = [className, active].filter(Boolean).join(" ");

  const buttonElement = (
    <AntButton
      {...buttonProps}
      ref={ref}
      className={combinedClassName || undefined}
    />
  );

  // Wrap with Tooltip if tooltip prop is provided
  if (tooltip) {
    return (
      <Tooltip
        tooltip={tooltip}
        tooltipPlacement={tooltipPlacement}
        overlayClassName={overlayClassName}
        getPopupContainer={getPopupContainer}
      >
        {buttonElement}
      </Tooltip>
    );
  }

  return buttonElement;
});

export default Button;
